import React, { useState } from "react";

/**
 * Renders a publication SVG logo as a single uniform muted cool grey (#8b8fa3)
 * using a CSS mask (alpha channel), so every logo is the exact same colour
 * regardless of its original fills, strokes, or gradients.
 *
 * SIZING — shrink-wrapped to true logo width:
 * An invisible <img> shim sizes the element to the logo's natural aspect ratio
 * at a fixed height (h-7 / h-8). Unlike a fixed-width box, the container only
 * occupies the space the logo actually needs — so a flex `gap` on the parent
 * produces truly uniform spacing between every logo regardless of individual
 * widths. `mask-size: contain` fills that fitted box with the logo art.
 *
 * OPTICAL BALANCE — per-logo `scale`:
 * A subtle transform nudges each logo so visual weight reads evenly across
 * the row. For SVGs with large internal whitespace (e.g. Digital Journal),
 * a larger scale zooms past the padding so the logo reads at a comparable
 * visual size to its neighbours.
 *
 * Hover brightens the logo toward white.
 */
export default function MarqueeLogo({ src, name, scale = 1, className = "", hideOnError = false, silhouette = false }) {
  const [failed, setFailed] = useState(false);
  const [hidden, setHidden] = useState(false);
  const maskStyle = {
    WebkitMaskImage: `url(${src})`,
    WebkitMaskRepeat: "no-repeat",
    WebkitMaskPosition: "center",
    WebkitMaskSize: "contain",
    maskImage: `url(${src})`,
    maskRepeat: "no-repeat",
    maskPosition: "center",
    maskSize: "contain",
    transform: `scale(${scale})`,
    transformOrigin: "center",
  };

  if (hidden) {
    // hideOnError mode: a failed/missing image hides this entire item wrapper
    // (display: none) so no empty gap is left in the marquee.
    return <span style={{ display: "none" }} aria-hidden="true" />;
  }

  if (failed) {
    // Image failed to load — render the outlet's text name in the same slot
    // sizing/colour as the text fallback so no item ever occupies width
    // without visible content (the root cause of the "gaps" in the marquee).
    return (
      <span
        className={`inline-flex shrink-0 items-center h-7 lg:h-8 font-heading text-sm font-bold text-[#6c6c74] group-hover/logo:text-[#1c1c20] transition-colors duration-300 ${className}`}
        title={name}
      >
        {name}
      </span>
    );
  }

  // Silhouette mode (Saudi + MENA trust strips): render the source image as a
  // uniform dark-grey silhouette via a single CSS filter, regardless of the
  // source file's colour. The default mask-based treatment below is bypassed so
  // the treatment is applied exactly once. Dimensions, spacing, scale, and the
  // hideOnError/failed fallbacks are unchanged.
  if (silhouette) {
    return (
      <span
        className={`relative inline-flex shrink-0 items-center justify-center group/logo ${className}`}
        title={name}
        role="img"
        aria-label={name}
        style={{ transform: `scale(${scale})`, transformOrigin: "center" }}
      >
        <img
          src={src}
          alt={name}
          width="140"
          height="28"
          decoding="async"
          onError={() => (hideOnError ? setHidden(true) : setFailed(true))}
          className="h-7 lg:h-8 w-auto max-w-[200px] select-none"
          style={{ filter: "brightness(0)", opacity: 0.65 }}
        />
      </span>
    );
  }

  return (
    <span
      className={`relative inline-flex shrink-0 items-center justify-center group/logo ${className}`}
      title={name}
      role="img"
      aria-label={name}
    >
      {/* Sizing shim — invisible img sizes the box to the logo's natural
          aspect ratio at the target height. Width is auto-computed. */}
      <img
        src={src}
        alt=""
        aria-hidden="true"
        width="140"
        height="28"
        decoding="async"
        onError={() => (hideOnError ? setHidden(true) : setFailed(true))}
        className="h-7 lg:h-8 w-auto max-w-[200px] opacity-0 select-none"
      />
      {/* Mono overlay — mask fills the shim's box with one uniform dark grey. */}
      <span
        className="absolute inset-0 bg-[#6c6c74] group-hover/logo:bg-[#1c1c20] transition-colors duration-300"
        style={maskStyle}
        aria-hidden="true"
      />
    </span>
  );
}